import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Eye, Send, Download, RefreshCw, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_LABELS = {
  da_preparare: "Da preparare", inviato: "Inviato", accettato: "Accettato",
  completato: "Completato", annullato: "Annullato", new: "Da preparare",
  contacted: "Contattato", completed: "Completato", cancelled: "Annullato", test: "Test",
};
const STATUS_COLORS = {
  da_preparare: "text-gold border-gold/50", new: "text-gold border-gold/50",
  inviato: "text-blue-700 border-blue-700/40", accettato: "text-emerald-700 border-emerald-700/40",
  completato: "text-emerald-700 border-emerald-700/40", completed: "text-emerald-700 border-emerald-700/40",
  annullato: "text-red-700 border-red-700/40", cancelled: "text-red-700 border-red-700/40",
  contacted: "text-taupe border-hair", test: "text-taupe border-hair",
};

export function ShopQuotesPanel({ items, reload }) {
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const open = (q) => {
    setSel(q);
    setForm({
      discount_percent: q.discount_percent ?? 0,
      transport_cost: q.transport_cost ?? 0,
      validity_days: q.validity_days ?? 15,
    });
  };

  const calc = () => {
    const list = (sel?.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
    const discount = Math.round(list * (Number(form.discount_percent) || 0)) / 100;
    const transport = Number(form.transport_cost) || 0;
    return { list, discount, transport, final: Math.round((list - discount + transport) * 100) / 100 };
  };

  const saveFields = async () => {
    const { data } = await api.put(`/admin/inquiries/${sel.id}/quote`, {
      discount_percent: Number(form.discount_percent) || 0,
      transport_cost: Number(form.transport_cost) || 0,
      validity_days: Number(form.validity_days) || 15,
    });
    return data;
  };

  const preview = async () => {
    setBusy(true);
    try {
      await saveFields();
      const { data } = await api.post(`/admin/inquiries/${sel.id}/quote/pdf`);
      window.open(absoluteMediaUrl(data.pdf_url), "_blank", "noopener");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(false); }
  };

  const generateAndSend = async () => {
    if (sending) return; // protezione doppio click
    setSending(true);
    try {
      await saveFields();
      await api.post(`/admin/inquiries/${sel.id}/quote/pdf`);
      const { data } = await api.post(`/admin/inquiries/${sel.id}/quote/send`);
      if (data.success) {
        toast.success("Prezzo esclusivo inviato al cliente (copia allo Studio)");
        setSel(null); reload();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Invio non riuscito"); reload(); }
    finally { setSending(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  if (sel && form) {
    const c = calc();
    const st = STATUS_LABELS[sel.status] || sel.status;
    return (
      <div className="space-y-6" data-testid="shop-quote-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => setSel(null)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="shop-quote-back">
            <ArrowLeft size={14} /> Tutte le richieste
          </button>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-3 py-1.5 ${STATUS_COLORS[sel.status] || "text-taupe border-hair"}`} data-testid="shop-quote-status">{st}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Cliente</div>
            <p className="font-serif text-xl">{sel.name}</p>
            <p className="text-sm text-taupe mt-1">{sel.email} · {sel.phone}</p>
            {sel.address && <p className="text-sm text-taupe">{sel.address}</p>}
            <p className="text-xs text-taupe mt-2">{fmtDate(sel.created_at)}</p>
            {sel.notes && <p className="text-sm text-taupe mt-3 whitespace-pre-wrap border-t border-hair/60 pt-3">{sel.notes}</p>}
          </div>
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Articoli (da listino, non modificabili)</div>
            {(sel.items || []).map((i, k) => (
              <div key={k} className="flex justify-between text-sm py-1.5 border-b border-hair/60 last:border-0">
                <span>{i.quantity}× {i.product_name}</span>
                <span className="text-taupe">{eur(i.price * i.quantity)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 mt-1 border-t border-hair font-medium">
              <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Totale di listino</span>
              <span data-testid="shop-quote-list-total">{eur(c.list)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6" data-testid="shop-quote-form">
          <div className="eyebrow mb-3">Prezzo esclusivo</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Percentuale di sconto (%)</Label>
              <Input type="number" min={0} max={90} step="0.5" value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                className="rounded-none mt-1" data-testid="shop-quote-discount" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Spese di trasporto (€, non scontabili)</Label>
              <Input type="number" min={0} step="0.01" value={form.transport_cost}
                onChange={(e) => setForm({ ...form, transport_cost: e.target.value })}
                className="rounded-none mt-1" data-testid="shop-quote-transport" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Validità prezzo (giorni)</Label>
              <Input type="number" min={1} value={form.validity_days}
                onChange={(e) => setForm({ ...form, validity_days: e.target.value })}
                className="rounded-none mt-1" data-testid="shop-quote-validity" />
            </div>
          </div>
          <div className="mt-5 border-t border-hair pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid="shop-quote-calc">
            <div><span className="text-[10px] tracking-[0.14em] uppercase text-taupe block">Listino</span><span className="font-serif text-lg">{eur(c.list)}</span></div>
            <div><span className="text-[10px] tracking-[0.14em] uppercase text-taupe block">Sconto {Number(form.discount_percent) || 0}%</span><span className="font-serif text-lg text-gold" data-testid="shop-quote-discount-value">− {eur(c.discount)}</span></div>
            <div><span className="text-[10px] tracking-[0.14em] uppercase text-taupe block">Trasporto</span><span className="font-serif text-lg">{eur(c.transport)}</span></div>
            <div><span className="text-[10px] tracking-[0.14em] uppercase text-taupe block">Prezzo esclusivo</span><span className="font-serif text-xl text-ink" data-testid="shop-quote-final">{eur(c.final)}</span></div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={preview} disabled={busy || sending} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="shop-quote-preview"><Eye size={14} /> Anteprima</button>
          <button onClick={generateAndSend} disabled={busy || sending} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="shop-quote-send">
            <Send size={14} /> {sending ? "Invio in corso…" : "Genera e invia prezzo esclusivo"}
          </button>
          {sel.pdf_url && (
            <>
              <a href={absoluteMediaUrl(sel.pdf_url)} target="_blank" rel="noreferrer" className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="shop-quote-download"><Download size={14} /> Scarica PDF</a>
              <button onClick={generateAndSend} disabled={busy || sending} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="shop-quote-resend"><RefreshCw size={14} /> Invia nuovamente</button>
            </>
          )}
        </div>

        {(sel.sent_log?.length > 0 || sel.versions?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-hair p-5" data-testid="shop-quote-sentlog">
              <div className="eyebrow mb-3">Invii</div>
              {(sel.sent_log || []).slice().reverse().map((s, i) => (
                <p key={i} className="text-xs text-taupe py-1 border-b border-hair/60 last:border-0">{fmtDate(s.at)} — {s.to} · {s.result}</p>
              ))}
              {(sel.sent_log || []).length === 0 && <p className="text-xs text-taupe">Nessun invio.</p>}
            </div>
            <div className="bg-white border border-hair p-5" data-testid="shop-quote-versions">
              <div className="eyebrow mb-3">Versioni precedenti</div>
              {(sel.versions || []).slice().reverse().map((v, i) => (
                <div key={i} className="text-xs text-taupe py-1 border-b border-hair/60 last:border-0">
                  <p>v{v.n} · {fmtDate(v.at)} · sconto {v.snapshot?.discount_percent}% · trasporto {eur(v.snapshot?.transport_cost || 0)}</p>
                  {v.snapshot?.pdf_url && <a href={absoluteMediaUrl(v.snapshot.pdf_url)} target="_blank" rel="noreferrer" className="text-gold hover:underline">Apri PDF versione</a>}
                </div>
              ))}
              {(sel.versions || []).length === 0 && <p className="text-xs text-taupe">Nessuna versione precedente.</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="shop-quotes-list">
      {items.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessuna richiesta dallo Shop</div>}
      {items.map((q) => (
        <button key={q.id} onClick={() => open(q)} className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors" data-testid={`shop-quote-row-${q.id}`}>
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-serif text-lg">{q.name}</span>
                <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${STATUS_COLORS[q.status] || "text-taupe border-hair"}`}>{STATUS_LABELS[q.status] || q.status}</span>
              </div>
              <p className="text-sm text-taupe mt-1 truncate">{(q.items || []).map((i) => `${i.quantity}× ${i.product_name}`).join(" · ")}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-serif text-lg">{eur(q.total)}</p>
              <p className="text-xs text-taupe">{fmtDate(q.created_at)}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
